<script setup>

import {transition_effects} from "@/constants";
import {onBeforeUnmount, onMounted, ref, watch} from "vue";
import {emitter} from "@/emitter";
import store from "@/vuex/store";

const showTransitionModal = ref(false);

const selected_effect = ref('fade')
const duration = ref(1)
const offset = ref(0)
const override = ref(false)

const clip_id = ref(null)
const clip = ref(null)

onMounted(() => {
  emitter.on('showTransitionModal', (index) => setVisibleModal(index));
})

onBeforeUnmount(() => {
  emitter.off('showTransitionModal', (index) => setVisibleModal(index));
})

function setVisibleModal(index) {
  showTransitionModal.value = true
  clip_id.value = index;
}

function getTransitionLabel(name) {
  return transition_effects.filter(t => t.name === name)[0]?.label
}

function applyTransition() {
  store.commit('live/applyEffectToClip', {
    id: clip_id.value,
    transition: {
      effect: selected_effect.value,
      duration: duration.value,
      offset: offset.value,
      label: getTransitionLabel(selected_effect.value)
    }
  })
  showTransitionModal.value = false
}

function removeTransitionFromAll() {
  store.dispatch('live/removeEffectFromAll')
  showTransitionModal.value = false
}

function removeTransition() {
  store.commit('live/applyEffectToClip', {id: clip_id.value, transition: null})
  showTransitionModal.value = false
}

function applyToAll() {
  store.dispatch('live/applyEffectToAll', {
    effect: selected_effect.value,
    duration: duration.value,
    offset: offset.value,
    label: getTransitionLabel(selected_effect.value),
    override: override.value,
  })
  showTransitionModal.value = false
}

watch(showTransitionModal, (v) => {
  if (!v) {
    selected_effect.value = 'fade';
    duration.value = 1;
    offset.value = 0;
    override.value = false;
    clip_id.value = null;
    clip.value = null;
  } else {
    if(!clip_id.value) return;
    clip.value = store.getters["live/getClipById"](clip_id.value).transition || null;
    if(!clip.value) return
    selected_effect.value = clip.value.effect;
    duration.value = clip.value.duration;
    offset.value = clip.value.offset;
    override.value = false;
  }
})

</script>

<template>
  <transition name="fade">
    <div v-if="showTransitionModal"
         class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-50 p-4"
         @click.self="showTransitionModal = false">
      <div
        class="bg-white p-8 rounded-[4px] shadow-2xl w-[480px] max-w-full relative overflow-hidden">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-xl font-bold text-dark_blue">Transition Settings</h3>
          <button @click="showTransitionModal = false" class="text-slate-500 hover:text-red-400 transition-colors">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <!-- Form -->
        <div class="space-y-3">
          <!-- Custom Searchable Select -->
          <div class="space-y-2 relative z-20">
            <label class="text-xs font-bold text-dark_blue tracking-wider">
              Effect Type (Type to filter)
            </label>
            <el-select v-model="selected_effect" class="w-full transition-select" size="large" filterable clearable
                       popper-class="transition-select">
              <el-option v-for="effect in transition_effects" :value="effect.name" :label="effect.label"
                         :key="effect.name"/>
            </el-select>
          </div>

          <div class="flex flex-1">
            <img :src="`/images/effects/${selected_effect}.gif`" alt="Effect" class="mx-auto">
          </div>

          <div class="flex gap-2 justify-between">
            <div class="space-y-2">
              <label class="text-xs font-bold text-dark_blue tracking-wider">Duration (Seconds)</label>
              <div class="flex items-center gap-2">
                <div class="relative flex-1">
                  <el-input-number v-model="duration" :min="0.01" :step="1" size="large" class="transition-select"
                                   @blur="duration ? '' : (duration = 0)"/>
                </div>
              </div>
            </div>
          </div>

          <hr>
          <!-- Apply Actions -->
          <div class="space-y-3">
            <button
              v-if="!clip_id"
              @click="applyToAll"
              class="w-full py-4 rounded-[4px] bg-blue-450 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 "
            >
              <i class="fas fa-layer-group"></i> Bulk Apply Transitions
            </button>

            <div v-if="!clip_id">
              <el-checkbox v-model="override" size="large" label="Override existing effects"/>
            </div>

            <!-- Split Action Area -->
            <div class="flex gap-3">
              <button
                v-if="!clip_id || clip"
                @click="clip_id ? removeTransition() : removeTransitionFromAll()"
                class="flex flex-1 py-3 items-center justify-center gap-2 px-2 rounded-[4px] border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 font-semibold text-sm transition-all"
              >
                <i class="fas fa-trash"></i> {{clip_id ? 'Remove' : 'Bulk Remove'}}
              </button>
              <button
                v-if="!clip_id || !clip"
                @click="() => showTransitionModal = false"
                class="flex-[2] py-3 rounded-[4px] bg-orange-450 text-white font-bold text-sm transition-all"
              >
                Cancel
              </button>
              <button
                v-if="clip_id"
                @click="applyTransition"
                class="flex-[2] py-3 rounded-[4px] bg-blue-450 text-white font-bold text-sm transition-all"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
